import * as React from "react";
import { CalendarCheck, CalendarX2, Clock, MapPin, Video } from "lucide-react";

import {
  apiErrorMessage,
  bookSlot,
  getPublicBookingPage,
} from "@/api/client";
import {
  BookSlotRequest,
  PublicBookingConfirmationView,
  PublicBookingPageView,
  PublicBookingSlot,
  SlotPagingBounds,
  SlotWindow,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichText } from "@/lib/richText";
import { cn } from "@/lib/utils";

import { BookingCalendar } from "./BookingCalendar";
import { BookingPageSkeleton, PublicLoadingLoop, useLoadingWindow } from "./public/PublicLoading";
import { noteForRequest } from "./inviteeNote";
import { canGoNext, canGoPrev, initialMonth, useSlotPaging } from "./useSlotPaging";
import { suggestEmail } from "./emailSuggest";
import "./PublicRollingLinkPage.css";
import { conferenceProviderLabel } from "@/lib/calendarProviders";
import { Card, HeaderCard, PublicFooter } from "./public/PublicChrome";

/**
 * The anonymous invitee page at {@code /book/{slug}} (or the {@code /@handle/ad-hoc/{name}}
 * only address). No login: view the host's open slots, pick one, leave a name +
 * email, and get a calendar invite. Shares the invitee look of {@link PublicRollingLinkPage}
 * — a banner header card, a tinted "what you're booking" card, then our own calendar —
 * on the landing palette (blue primary, teal accent, coral highlight). The blue primary
 * comes from PublicRollingLinkPage.css, scoped to .inv-theme.
 */



type Load =
  | { status: "loading" }
  | { status: "error"; kind: "not-found" | "unreachable" }
  | { status: "ready"; page: PublicBookingPageView };

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/**
 * The unguessable slug link, {@code /book/{slug}}, which is the page's only address. A branded
 * {@code /@handle/ad-hoc/{name}} alias used to exist beside it and was removed: the slug IS the
 * access control here, and an alias derived from the page title was guessable from the handle.
 */
export function PublicBookingPage({ slug }: { slug: string }) {
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [picked, setPicked] = React.useState<PublicBookingSlot | null>(null);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmed, setConfirmed] = React.useState<PublicBookingConfirmationView | null>(null);
  const emailSuggestion = React.useMemo(() => suggestEmail(email), [email]);

  const fetchPage = React.useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const page = await getPublicBookingPage(slug);
      setLoad({ status: "ready", page });
    } catch (e) {
      // A 404 is a definitive answer about the link itself; anything else — a 5xx, or a
      // dropped/timed-out connection that never produced a response at all — means we
      // simply couldn't reach the server, which is transient and worth retrying.
      const notFound =
        typeof e === "object" && e !== null && "status" in e && (e as { status: number }).status === 404;
      setLoad({ status: "error", kind: notFound ? "not-found" : "unreachable" });
    }
  }, [slug]);

  React.useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  // The first response carries the link's bounds and its first month of slots; every later month is
  // fetched on demand and kept, so paging back is instant. See useSlotPaging.
  const bounds: SlotPagingBounds = load.status === "ready" ? load.page : {};
  const fetchMonth = React.useCallback(
    (window: SlotWindow) => getPublicBookingPage(slug, window).then((v) => v.slots),
    [slug],
  );
  const seed = React.useMemo(
    () =>
      load.status === "ready"
        ? { slots: load.page.slots, month: initialMonth(load.page) }
        : null,
    [load],
  );
  const paging = useSlotPaging<PublicBookingSlot>(bounds, seed, fetchMonth);

  const submit = async () => {
    if (!picked || load.status !== "ready") return;
    setSubmitting(true);
    setError(null);
    const req: BookSlotRequest = {
      start: picked.start,
      name,
      email,
      note: noteForRequest(note),
      website,
    };
    try {
      // Book against the canonical slug the resolver returned.
      setConfirmed(await bookSlot(load.page.slug, req));
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't book that slot"));
      // A conflict likely means someone just took it — refresh the open slots.
      void fetchPage();
      setPicked(null);
    } finally {
      setSubmitting(false);
    }
  };

  const wide = load.status === "ready" && !confirmed && load.page.bookable && !picked;

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <div className="inv-theme min-h-screen bg-muted/30 px-4 py-10">
      <div className={cn("mx-auto flex flex-col gap-5", wide ? "max-w-3xl" : "max-w-xl")}>
        {showLoading && (
          <Card className="p-6">
            {/* The loop on top, the grid below it. The colour is the status; the grey is
                the shape of what is coming. Never the other way round, and never
                overlaid: artwork ON the grid points at the middle of a card that is
                about to redraw completely. */}
            <PublicLoadingLoop className="pt-0 pb-8" />
            <BookingPageSkeleton />
          </Card>
        )}

        {!showLoading && load.status === "error" && (
          <Card className="p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
              <CalendarX2 className="h-6 w-6 text-amber-700 dark:text-amber-300" />
            </div>
            {load.kind === "not-found" ? (
              <>
                <h1 className="text-lg font-bold text-foreground">
                  This link has expired or doesn't exist
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Booking links are only live for a limited time. Ask whoever shared it for a new one.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-lg font-bold text-foreground">We couldn't reach the server</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your connection may have dropped. The link is probably fine. Give it another go.
                </p>
                <Button className="mt-4" onClick={() => void fetchPage()}>
                  Try again
                </Button>
              </>
            )}
          </Card>
        )}

        {!showLoading && load.status === "ready" && confirmed && (
          <>
            <HeaderCard
              name={confirmed.host_name}
              subtitle="Schedule a meeting"
              avatarUrl={load.page.host_avatar_url}
              bannerUrl={load.page.host_banner_url}
              socialLinks={load.page.host_social_links}
            />
            <Card className="p-6 text-center">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/40">
                <CalendarCheck className="h-6 w-6 text-green-700 dark:text-green-300" />
              </div>
              <h1 className="text-lg font-bold text-foreground">You're booked!</h1>
              <p className="mt-1 text-sm">
                <span className="font-semibold">{confirmed.title}</span> with {confirmed.host_name}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {dayLabel(confirmed.start)}, {timeLabel(confirmed.start)}–{timeLabel(confirmed.end)}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                A calendar invite is on its way to {confirmed.invitee_email}
                {confirmed.meeting_url ? ", with your join link inside" : ""}.
              </p>
              {confirmed.location && (
                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm text-foreground">
                  <MapPin className="h-4 w-4 shrink-0 text-amber-600" /> {confirmed.location}
                </p>
              )}
            </Card>
          </>
        )}

        {!showLoading && load.status === "ready" && !confirmed && (
          <>
            <HeaderCard
              name={load.page.host_name}
              subtitle="Schedule a meeting"
              avatarUrl={load.page.host_avatar_url}
              bannerUrl={load.page.host_banner_url}
              socialLinks={load.page.host_social_links}
            />

            <Card className="p-6 sm:p-7">
              <h2 className="mb-3 text-sm font-bold text-foreground">What you're booking</h2>
              <div className="flex items-start gap-3 rounded-xl border border-[#bfe0ef] bg-[#e9f3f7] p-4 dark:border-[#2779a7]/50 dark:bg-[#1e5f83]/20">
                <span className="grid h-9 w-9 flex-none place-items-center rounded-lg border border-[#bfe0ef] bg-card text-[#2779a7] dark:border-[#2779a7]/50 dark:text-[#7cc3e6]">
                  <CalendarCheck className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-[#1e5f83] dark:text-[#cfe6f2]">{load.page.title}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-semibold uppercase tracking-wide text-[#2779a7] dark:text-[#7cc3e6]">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" /> {load.page.slot_minutes} min
                    </span>
                    {load.page.conferencing_provider && (
                      <span className="inline-flex items-center gap-1">
                        <Video className="h-3.5 w-3.5" /> {conferenceProviderLabel(load.page.conferencing_provider)}
                      </span>
                    )}
                    {load.page.location && (
                      <span className="inline-flex items-center gap-1 normal-case">
                        <MapPin className="h-3.5 w-3.5" /> {load.page.location}
                      </span>
                    )}
                  </p>
                  {load.page.description && (
                    <RichText
                      text={load.page.description}
                      className="mt-2 text-sm leading-relaxed text-foreground"
                    />
                  )}
                  {load.page.conferencing_provider && (
                    <p className="mt-2 text-sm font-medium text-[#2779a7] dark:text-[#7cc3e6]">
                      A {conferenceProviderLabel(load.page.conferencing_provider)} link is sent when you book.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                {!load.page.bookable ? (
                  <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                    {load.page.unavailable_reason ?? "No times are currently available."}
                  </p>
                ) : !picked ? (
                  <>
                    <h3 className="mb-3 text-sm font-bold text-foreground">Available times</h3>
                    <BookingCalendar
                      slots={paging.slots}
                      slotMinutes={load.page.slot_minutes}
                      allowTimezoneChange
                      onPick={(s) => {
                        setPicked(s);
                        setError(null);
                      }}
                      viewMonth={paging.month}
                      onMonthChange={paging.goToMonth}
                      canGoPrev={canGoPrev(paging.month, bounds)}
                      canGoNext={canGoNext(paging.month, bounds)}
                      loadingMonth={paging.loading}
                    />
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-[#bfe0ef] bg-[#e9f3f7] p-3 text-sm dark:border-[#2779a7]/50 dark:bg-[#1e5f83]/20">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground">
                          <span className="font-semibold">{dayLabel(picked.start)}</span>,{" "}
                          {timeLabel(picked.start)}–{timeLabel(picked.end)}
                        </span>
                        <button
                          type="button"
                          className="text-xs font-semibold text-[#2779a7] hover:underline dark:text-[#7cc3e6]"
                          onClick={() => setPicked(null)}
                        >
                          change
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="bk-name">Your name</Label>
                      <Input id="bk-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="bk-email">Email</Label>
                      <Input
                        id="bk-email"
                        type="email"
                        value={email}
                        maxLength={254}
                        placeholder="you@example.com"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      {emailSuggestion && (
                        <p className="text-[11px] text-muted-foreground">
                          Did you mean{" "}
                          <button
                            type="button"
                            className="font-semibold text-[#2779a7] underline underline-offset-2 dark:text-[#7cc3e6]"
                            onClick={() => setEmail(emailSuggestion)}
                          >
                            {emailSuggestion}
                          </button>
                          ?
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="bk-note">Anything to add? (optional)</Label>
                      <textarea
                        id="bk-note"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        value={note}
                        maxLength={500}
                        rows={2}
                        onChange={(e) => setNote(e.target.value)}
                      />
                      <p
                        className={cn(
                          "text-right text-xs tabular-nums",
                          note.length >= 500
                            ? "font-semibold text-rose-600 dark:text-rose-400"
                            : note.length >= 450
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground",
                        )}
                      >
                        {note.length} / 500
                      </p>
                    </div>
                    {/* Honeypot: hidden from humans, catnip for bots. */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      className="hidden"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />

                    {error && (
                      <p className="rounded-xl border border-rose-300 bg-rose-50 p-2 text-sm text-rose-700 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-200">
                        {error}
                      </p>
                    )}

                    <Button
                      className="w-full bg-[#df6c4f] text-white hover:bg-[#c85b40]"
                      onClick={submit}
                      disabled={submitting || email.trim() === ""}
                    >
                      {submitting ? "Booking…" : "Confirm booking"}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        <PublicFooter />
      </div>
    </div>
  );
}
